import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { TaskySelect } from '../tasky';
import {
  fetchPopulationHistogram,
  MetricHistogram,
  PopulationHistogramData,
} from '../../services/api/adScoreService';

interface AdScoreHistogramProps {
  predictedObservations?: number;
  predictedPlays?: number;
  media?: string;
}

interface HistogramBar {
  binStart: number;
  binEnd: number;
  count: number;
  index: number;
}

// Convert a log1p value back to original scale
function expm1(x: number): number {
  return Math.expm1(x);
}

// Format large numbers for display
function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return Math.round(n).toLocaleString();
}

// Build chart data from histogram bins
function buildChartData(histogram: MetricHistogram): HistogramBar[] {
  const data: HistogramBar[] = [];
  for (let i = 0; i < histogram.counts.length; i++) {
    data.push({
      binStart: histogram.bins[i],
      binEnd: histogram.bins[i + 1],
      count: histogram.counts[i],
      index: i,
    });
  }
  return data;
}

// Find the bin index for a given raw value
function findBinIndex(rawValue: number, bins: number[]): number {
  if (!bins || bins.length < 2) return -1;

  const log1pValue = Math.log1p(rawValue);

  // Below minimum bin edge
  if (log1pValue < bins[0]) {
    return 0;
  }

  for (let i = 0; i < bins.length - 1; i++) {
    if (log1pValue >= bins[i] && log1pValue < bins[i + 1]) {
      return i;
    }
  }
  // If value exceeds max bin, return last bin
  return bins.length - 2;
}

// Custom tooltip
function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: HistogramBar }>;
}) {
  if (!active || !payload || !payload[0]) return null;
  const bar = payload[0].payload;
  const origStart = expm1(bar.binStart);
  const origEnd = expm1(bar.binEnd);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <div className="text-gray-600">
        {formatNumber(origStart)} ~ {formatNumber(origEnd)}
      </div>
      <div className="font-medium text-gray-900">
        {bar.count.toLocaleString()} creatives
      </div>
    </div>
  );
}

// Single metric histogram chart
function MetricChart({
  title,
  histogram,
  predictedValue,
  color,
}: {
  title: string;
  histogram: MetricHistogram;
  predictedValue?: number;
  color: string;
}) {
  const chartData = useMemo(() => buildChartData(histogram), [histogram]);

  const predictedBinIndex = useMemo(() => {
    if (predictedValue == null) return -1;
    return findBinIndex(predictedValue, histogram.bins);
  }, [predictedValue, histogram.bins]);

  // Generate tick values: show original scale labels at key points
  const ticks = useMemo(() => {
    const maxBin = histogram.bins[histogram.bins.length - 1];
    const tickCount = 5;
    const result: number[] = [];
    for (let i = 0; i <= tickCount; i++) {
      result.push((maxBin / tickCount) * i);
    }
    return result;
  }, [histogram.bins]);

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 10, left: 0, bottom: 20 }}
        >
          <XAxis
            dataKey="binStart"
            type="number"
            domain={[0, 'dataMax']}
            ticks={ticks}
            tickFormatter={(v: number) => formatNumber(expm1(v))}
            tick={{ fontSize: 10 }}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            tickFormatter={(v: number) => formatNumber(v)}
            label={{
              value: 'creatives',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              fontSize: 10,
              fill: '#9CA3AF',
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" isAnimationActive={false}>
            {chartData.map((_, i) => (
              <Cell
                key={i}
                fill={i === predictedBinIndex ? color : '#D1D5DB'}
                fillOpacity={i === predictedBinIndex ? 0.9 : 0.6}
              />
            ))}
          </Bar>
          {predictedValue != null && (
            <ReferenceLine
              x={Math.log1p(predictedValue)}
              stroke={color}
              strokeDasharray="4 2"
              strokeWidth={1.5}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
      {/* Stats */}
      <div className="flex gap-3 mt-1 text-[10px] text-gray-400">
        <span>Median: {formatNumber(expm1(histogram.stats.median))}</span>
        <span>Mean: {formatNumber(expm1(histogram.stats.mean))}</span>
        <span>N={histogram.stats.total.toLocaleString()}</span>
      </div>
      {predictedValue != null && (
        <div className="mt-1 text-xs" style={{ color }}>
          Predicted: {formatNumber(predictedValue)}
        </div>
      )}
    </div>
  );
}

const AdScoreHistogram: React.FC<AdScoreHistogramProps> = ({
  predictedObservations,
  predictedPlays,
  media: initialMedia,
}) => {
  const [data, setData] = useState<PopulationHistogramData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<string>(
    initialMedia || ''
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchPopulationHistogram(
          selectedMedia || undefined
        );
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        console.error('Failed to fetch histogram data:', err);
        if (!cancelled) {
          setError('Distribution data could not be loaded');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [selectedMedia]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-sm text-gray-400">
        Loading distribution...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-xs text-gray-400 py-4 text-center">
        {error || 'No data available'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Media selector */}
      {data.available_media && data.available_media.length > 0 && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Media:</label>
          <TaskySelect
            value={selectedMedia}
            onChange={(e) => setSelectedMedia(e.target.value)}
            className="h-8 text-sm"
          >
            <option value="">All</option>
            {data.available_media.filter((m) => m !== 'all').map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </TaskySelect>
        </div>
      )}

      {/* Histograms */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <MetricChart
          title="Observations"
          histogram={data.observations}
          predictedValue={predictedObservations}
          color="#EF4444"
        />
        <MetricChart
          title="Play Count"
          histogram={data.plays}
          predictedValue={predictedPlays}
          color="#3B82F6"
        />
      </div>
    </div>
  );
};

export default AdScoreHistogram;
